import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { uploadAttachment, useSubmitScopeRequest } from "@/features/rfq/api/requests";
import { uploadErrorMessage } from "@/utils/managedFile";

/**
 * Wniosek podwykonawcy o rozszerzenie zakresu.
 *
 * Firma nie wybiera, czy chce kwalifikacji pełnej czy warunkowej — to decyzja GK.
 * Podaje tylko, o co się ubiega i dlaczego, opcjonalnie z załącznikami.
 *
 * Dwa poziomy proszenia: NOWE kategorie (w całości, bo cudzych pozycji firma nie
 * widzi) oraz POJEDYNCZE roboty w kategorii, którą już ma, ale zawężoną.
 */

interface Position {
  id: string;
  name: string;
  granted: boolean;
}

interface Category {
  id: string;
  name: string;
  granted: boolean;
  all_positions: boolean;
  positions: Position[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  voivodeships: { value: string; label: string; granted: boolean }[];
}

export function ScopeExtensionRequestModal({
  open,
  onClose,
  categories,
  voivodeships,
}: Props) {
  const submit = useSubmitScopeRequest();
  const [pickedCategories, setPickedCategories] = useState<string[]>([]);
  const [pickedPositions, setPickedPositions] = useState<string[]>([]);
  const [pickedAreas, setPickedAreas] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const heldAreas = voivodeships.filter((v) => v.granted).map((v) => v.value);

  // Posiadane województwa startują zaznaczone — formularz ma odbijać stan faktyczny,
  // a nie udawać, że firma zaczyna od zera.
  useEffect(() => {
    if (open) setPickedAreas(heldAreas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, voivodeships.length]);

  const newCategories = categories.filter((c) => !c.granted);
  // Doprosić o pojedynczą robotę da się tylko tam, gdzie kwalifikacja jest zawężona.
  const narrowed = categories.filter(
    (c) => c.granted && !c.all_positions && c.positions.some((p) => !p.granted),
  );

  const toggle = (list: string[], set: (v: string[]) => void, value: string) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const reset = () => {
    setPickedCategories([]);
    setPickedPositions([]);
    setDescription("");
    setFiles([]);
    setError("");
  };

  const handleSubmit = async () => {
    const newAreas = pickedAreas.filter((v) => !heldAreas.includes(v));
    if (!pickedCategories.length && !pickedPositions.length && !newAreas.length) {
      setError("Proszę wskazać co najmniej jedną kategorię, pozycję albo województwo.");
      return;
    }
    if (!description.trim()) {
      setError("Proszę uzasadnić wniosek.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      // Załączniki idą do wspólnego rejestru plików; wniosek dostaje same identyfikatory.
      const attachments = await Promise.all(files.map(uploadAttachment));
      await submit.mutateAsync({
        categories: pickedCategories,
        positions: pickedPositions,
        voivodeships: newAreas,
        description: description.trim(),
        attachments,
      });
      reset();
      onClose();
    } catch (err) {
      setError(uploadErrorMessage(err, "Nie udało się złożyć wniosku. Proszę spróbować ponownie."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Wniosek o rozszerzenie zakresu" size="lg">
      <div className="space-y-5">
        <Section label="Nowe kategorie prac">
          {newCategories.length === 0 ? (
            <Hint>Mają Państwo już wszystkie kategorie.</Hint>
          ) : (
            <div className="grid gap-1 sm:grid-cols-2">
              {newCategories.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={pickedCategories.includes(c.id)}
                    onChange={() => toggle(pickedCategories, setPickedCategories, c.id)}
                  />
                  <span className="text-[var(--color-text-primary)]">{c.name}</span>
                </label>
              ))}
            </div>
          )}
        </Section>

        {narrowed.length > 0 && (
          <Section label="Rozszerzenie w posiadanych kategoriach">
            <Hint>
              Kategorie, które mają Państwo przyznane w zawężonym zakresie — można
              doprosić o kolejne roboty.
            </Hint>
            <div className="mt-2 space-y-3">
              {narrowed.map((category) => (
                <div key={category.id}>
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    {category.name}
                  </p>
                  <div className="mt-1 space-y-0.5 border-l border-[var(--color-border)] pl-3">
                    {category.positions
                      .filter((p) => !p.granted)
                      .map((position) => (
                        <label key={position.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={pickedPositions.includes(position.id)}
                            onChange={() =>
                              toggle(pickedPositions, setPickedPositions, position.id)
                            }
                          />
                          <span className="text-[var(--color-text-primary)]">
                            {position.name}
                          </span>
                        </label>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section label="Województwa">
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
            {voivodeships.map((v) => (
              <label
                key={v.value}
                className="flex items-center gap-2 text-sm"
                title={v.granted ? "Już przyznane" : undefined}
              >
                <input
                  type="checkbox"
                  checked={pickedAreas.includes(v.value)}
                  disabled={v.granted}
                  onChange={() => toggle(pickedAreas, setPickedAreas, v.value)}
                />
                <span
                  className={
                    v.granted
                      ? "text-[var(--color-text-muted)]"
                      : "text-[var(--color-text-primary)]"
                  }
                >
                  {v.label}
                </span>
              </label>
            ))}
          </div>
        </Section>

        <div>
          <label className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
            Uzasadnienie
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="Doświadczenie, zrealizowane zlecenia, posiadany sprzęt i uprawnienia…"
            className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
          />
        </div>

        <div>
          <label className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
            Załączniki (opcjonalnie)
          </label>
          <FileDropZone files={files} onFilesChange={setFiles} multiple />
          <Hint>Referencje, uprawnienia, certyfikaty, pozostałe dokumenty.</Hint>
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Anuluj
          </Button>
          <Button onClick={handleSubmit} disabled={busy}>
            {busy ? "Wysyłanie…" : "Złóż wniosek"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
        {label}
      </p>
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-[var(--color-text-muted)]">{children}</p>;
}
